import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  Tooltip,
  Checkbox,
  TableRow,
  TableCell,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import { Folder } from '../../types';

export interface FolderListItemProps {
  folder: Folder;
  onClick?: () => void;
  onOperation: (operation: string, folderId: string, data?: any) => void;
  isSelected: boolean;
  onSelectionChange: (selected: boolean) => void;
  listVariant?: 'default' | 'trash';
  layout?: 'list' | 'table';
  isSelectionMode?: boolean;
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const FolderListItem: React.FC<FolderListItemProps> = ({
  folder,
  onClick,
  onOperation,
  isSelected,
  onSelectionChange,
  listVariant = 'default',
  layout = 'table',
  isSelectionMode = false,
}) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const isTrash = listVariant === 'trash';

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.blur();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action: () => void) => {
    handleClose();
    action();
  };

  const handleItemClick = () => {
    if (isTrash) {
      return;
    }
    onClick?.();
    navigate(`/files/${folder.id}`);
  };

  const handleStarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onOperation('star', folder.id, !folder.isStarred);
  };

  const handleCheckboxClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onSelectionChange(!isSelected);
  };

  const folderIcon = (
    <Avatar
      variant="rounded"
      sx={{
        width: 36,
        height: 36,
        bgcolor: 'primary.light',
        color: 'primary.main',
      }}
    >
      <FolderIcon />
    </Avatar>
  );

  const sizeLabel =
    folder.itemCount !== undefined ? `${folder.itemCount} items` : '—';

  const contextMenu = (
    <Menu
      id="folder-list-menu"
      MenuListProps={{
        'aria-labelledby': `folder-list-menu-${folder.id}`,
      }}
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
    >
      {isTrash ? (
        <>
          <MenuItem onClick={() => handleAction(() => onOperation('restore', folder.id))}>
            <RestoreFromTrashIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
            Restore
          </MenuItem>
          <MenuItem
            onClick={() => handleAction(() => onOperation('delete', folder.id))}
            sx={{ color: 'error.main' }}
          >
            Delete permanently
          </MenuItem>
        </>
      ) : (
        <>
          <MenuItem onClick={() => handleAction(() => onClick?.())}>Open</MenuItem>
          <MenuItem onClick={() => handleAction(() => onOperation('download', folder.id))}>Download</MenuItem>
          <MenuItem onClick={() => handleAction(() => onOperation('share', folder.id))}>Share</MenuItem>
          <MenuItem onClick={() => handleAction(() => onOperation('rename', folder.id))}>Rename</MenuItem>
          <MenuItem onClick={() => handleAction(() => onOperation('copy', folder.id))}>Make a copy</MenuItem>
          <MenuItem onClick={() => handleAction(() => onOperation('move', folder.id))}>Move</MenuItem>
          <MenuItem onClick={() => handleAction(() => onOperation('star', folder.id, !folder.isStarred))}>
            {folder.isStarred ? 'Remove from favorites' : 'Add to favorites'}
          </MenuItem>
          <MenuItem onClick={() => handleAction(() => onOperation('properties', folder.id))}>
            Properties
          </MenuItem>
          <MenuItem
            onClick={() => handleAction(() => onOperation('delete', folder.id))}
            sx={{ color: 'error.main' }}
          >
            Delete
          </MenuItem>
        </>
      )}
    </Menu>
  );

  if (layout === 'table') {
    return (
      <>
        <TableRow
          hover
          selected={isSelected}
          sx={{ cursor: isTrash ? 'default' : 'pointer' }}
          onClick={handleItemClick}
          onContextMenu={handleClick}
        >
          <TableCell padding="checkbox">
            {isSelectionMode && (
              <Checkbox checked={isSelected} onClick={handleCheckboxClick} />
            )}
          </TableCell>
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
              {folderIcon}
              <Tooltip title={folder.name} placement="top">
                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                  {folder.name}
                </Typography>
              </Tooltip>
            </Box>
          </TableCell>
          <TableCell>
            <Typography variant="body2" color="text.secondary">
              Folder
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body2" color="text.secondary">
              {sizeLabel}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body2" color="text.secondary" noWrap>
              {folder.updatedAt ? formatDate(folder.updatedAt) : '—'}
            </Typography>
          </TableCell>
          <TableCell align="right" onClick={(e) => e.stopPropagation()}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              {!isTrash && (
                <IconButton
                  size="small"
                  aria-label="star"
                  onClick={handleStarClick}
                  sx={{ color: folder.isStarred ? 'warning.main' : 'action.disabled' }}
                >
                  {folder.isStarred ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                </IconButton>
              )}
              <IconButton
                size="small"
                aria-label="more"
                id={`folder-list-menu-${folder.id}`}
                aria-controls={open ? 'folder-list-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup="true"
                onClick={handleClick}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>
          </TableCell>
        </TableRow>
        {contextMenu}
      </>
    );
  }

  return (
    <ListItem
      alignItems="flex-start"
      sx={{
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
      onClick={handleItemClick}
      onContextMenu={handleClick}
      secondaryAction={
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Checkbox
            edge="end"
            checked={isSelected}
            onClick={handleCheckboxClick}
            sx={{ mr: 1 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
            {folder.itemCount !== undefined ? `${folder.itemCount} items` : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2, minWidth: 120 }}>
            {formatDate(folder.updatedAt || '')}
          </Typography>
          {!isTrash && (
            <IconButton
              edge="end"
              aria-label="star"
              onClick={handleStarClick}
              sx={{ color: folder.isStarred ? 'warning.main' : 'action.disabled', mr: 1 }}
            >
              {folder.isStarred ? <StarIcon /> : <StarBorderIcon />}
            </IconButton>
          )}
          <IconButton
            edge="end"
            aria-label="more"
            id={`folder-list-menu-${folder.id}`}
            aria-controls={open ? 'folder-list-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="true"
            onClick={handleClick}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>
      }
    >
      <ListItemAvatar>{folderIcon}</ListItemAvatar>
      <ListItemText
        primary={
          <Tooltip title={folder.name} placement="top">
            <Typography variant="body1" noWrap sx={{ maxWidth: '300px', fontWeight: 'medium' }}>
              {folder.name}
            </Typography>
          </Tooltip>
        }
        secondary={<Typography variant="body2" color="text.secondary">Folder</Typography>}
      />
      {contextMenu}
    </ListItem>
  );
};

export default FolderListItem;
